import { Avatar, IconButton, ThemeProvider, Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import CircleIcon from "@mui/icons-material/Circle";
import { useParams } from "react-router-dom";
import { getBrandDetails, getSessionInfoByAI, getSessionInfoByHub } from "./ApiService";
import CustomMUITheme from "../../CustomMUITheme";
import ChatSkeleton from "./ChatSkeleton";
import useIsMobile from "./useIsMobile";
import usePageMeta from "./usePageMeta";
import MainChatView from "./MainChatView";
import PageNotFound from "./PageNotFound";
import HelmetMeta from "./HelmetMeta";

const AgentChatWidget = () => {
  const isMobile = useIsMobile();
  const { source, uniqueId } = useParams();
  const [sessionData, setSessionData] = useState<any>({});
  const [brandDetails, setBrandDetails] = useState<any>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isEndedSession, setIsEndedSession] = useState<boolean>(false);
  const [isDisabledEndSessionBtn, setIsDisabledEndSessionBtn] = useState<boolean>(false);
  const [isSessionDataAvailable, setIsSessionDataAvailable] = useState(false);
  const [OgMeta, setOgMeta] = useState<any>({});
  const [recruiterInfo, setRecruiterInfo] = useState<any>();

  usePageMeta("AI | Agent", "Propose a Job - Streamline Your Workflow");

  useEffect(() => {
    sessionStorage.removeItem("sessionEnded");
  }, []);

  useEffect(() => {
    const fetchSessionInfo = async () => {
      setIsLoading(true);
      try {
        const response = source?.toLowerCase() === "a"
          ? await getSessionInfoByAI({ sessionId: uniqueId })
          : await getSessionInfoByHub(uniqueId);
  
        if (response.objectData) {
          if(response.objectData.og_tag_data){
            setOgMeta({
              title: response?.objectData.og_tag_data?.title || "AI Agent Chat",
              description: response?.objectData.og_tag_data?.description || "Engage with our AI Agent for personalized assistance.",
              image: response?.objectData.og_tag_data?.image || "",
            });
          }
          
          let parsedObjectData = typeof response.objectData === 'string' ? JSON.parse(response.objectData) : response.objectData || {};

          // Ensure session_data exists before assigning uniqueCode
          if (!parsedObjectData.session_data) {
            parsedObjectData.session_data = {};
          }

          if (source?.toLowerCase() === 'h' && response?.uniqueCode) {
            parsedObjectData.session_data['uniqueCode'] = response.uniqueCode;
          }

          setSessionData({
            ...response,
            objectData: parsedObjectData,
          });
          const contactRecruiter = parsedObjectData?.session_data?.contactRecruiter;
          if (contactRecruiter) {
            const { recruiterImage } = contactRecruiter;

            if (recruiterImage && recruiterImage.includes('upload/')) {
              const [prefix, suffix] = recruiterImage.split('upload/');
              const transformation = `c_thumb,g_face,q_auto,r_max,${isMobile ? "w_32" : "w_64"},${isMobile ? "h_32" : "h_64"}`;
              if (suffix && suffix.includes('/')) {
                const [, ...rest] = suffix.split('/');
                const updatedPath = rest.join('/');
                contactRecruiter.recruiterImage = `${prefix}upload/${transformation}/${updatedPath}`;
              } else {
                contactRecruiter.recruiterImage = `${prefix}upload/${transformation}/${suffix || ''}`;
              }
            }
            setRecruiterInfo(contactRecruiter);
          }
  
          // Fetch brand details now
          await fetchBrandDetails(response.clientCode);
        } else {
          setIsSessionDataAvailable(false);
        }
      } catch (error) {
        console.error("Failed to fetch session info:", error);
        setIsSessionDataAvailable(false);
      } finally {
        setIsLoading(false);
      }
    };
  
    const fetchBrandDetails = async (clientCode: string) => {
      try {
        const brandResponse = await getBrandDetails(clientCode);
        if (brandResponse.aiWidgetFormat && Object.keys(brandResponse.aiWidgetFormat).length > 0) {
          setBrandDetails(brandResponse.aiWidgetFormat);
          setIsSessionDataAvailable(true);
        }
      } catch (error) {
        console.error("Failed to fetch brand details:", error);
        setIsSessionDataAvailable(false);
      }
    };
  
    fetchSessionInfo();
  }, [source, uniqueId]);
  

  const handleDisabledEndSessionBtn = (status: boolean) => {
    setIsDisabledEndSessionBtn(status);
  };


  return (
    <ThemeProvider theme={CustomMUITheme("#092DAE", "#333333")}>
        <div className="flex items-center justify-center h-screen bg-gray-100" style={{ backgroundImage: `url(${brandDetails.backgroundImageUrl})`, backgroundSize: "cover", backgroundRepeat: "no-repeat" }}>
          <div className={`bg-white ${isMobile ? "h-full w-full": "w-[60%] h-[90%] rounded-[20px] overflow-hidden"}`} style={{ backgroundColor: brandDetails.mainBackgroundColor }}>
            {isLoading ? <ChatSkeleton /> : isSessionDataAvailable ?
              <>
                <header className={`flex items-center justify-between ${isMobile ? "px-4 h-[40px]": "px-6 h-[64px]" }`} style={{ backgroundColor: brandDetails.headerBackgroundColor }}>
                  <img className={`w-auto ${isMobile ? "h-[18px]": "h-[30px]" }`} alt="Client Logo" src={brandDetails.logoUrl} />
                </header>
                <header className={`flex items-center justify-between border-b1-Gray4 ${isMobile ? "pl-4 pr-2 h-[48px]": "pl-6 pr-4 h-[112px]"}`}>
                  <div className={`flex items-center ${isMobile ? "gap-2": "gap-4"}`}>
                    <Avatar alt="Assistant" sx={{ width: isMobile ? 32 : 64, height: isMobile ? 32 : 64, bgcolor: "#092DAE" }} src={recruiterInfo.recruiterImage} />
                    <div>
                      <div className={`flex items-center ${isMobile ? "gap-1": "gap-2"}`}>
                        <div className={`font-semibold text-gray1 truncate ${isMobile ? "leading-4": "text-[24px] leading-8"}`}>{recruiterInfo.firstName} {recruiterInfo.lastName}</div>
                        <div className="flex items-center gap-1 mt-1">
                          <IconButton disableRipple disableFocusRipple sx={{ padding: 0 }}>
                            <CircleIcon className="text-[#828282]" sx={{ width: isMobile ? 4: 8, height: isMobile ? 4: 8 }} />
                          </IconButton>
                          <div className={`text-gray3 ${isMobile ? "text-[8px] leading-[8px]": "leading-5"}`}>Offline</div>
                        </div>
                      </div>
                      <div className={`text-gray3 ${isMobile ? "text-[10px] leading-[10px] mt-[6px]": "text-[16px] leading-6 mt-2"}`}>{recruiterInfo.bio?.role || 'Recruiter'} bij {recruiterInfo.clientOrganization}</div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Tooltip title="Widget" placement="top" arrow>
                      <IconButton className="opacity-50 cursor-not-allowed">
                        <svg width={isMobile ? 24: 32} height={isMobile ? 24: 32} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6.61538 21.3015V16.8462H3.53846C3.13044 16.8462 2.73912 16.6841 2.45061 16.3955C2.16209 16.107 2 15.7157 2 15.3077V4.53846C2 4.13044 2.16209 3.73912 2.45061 3.45061C2.73912 3.16209 3.13044 3 3.53846 3H20.4615C20.8696 3 21.2609 3.16209 21.5494 3.45061C21.8379 3.73912 22 4.13044 22 4.53846V15.3077C22 15.7157 21.8379 16.107 21.5494 16.3955C21.2609 16.6841 20.8696 16.8462 20.4615 16.8462H12L7.27231 21.5738C7.21852 21.6278 7.14992 21.6645 7.07522 21.6794C7.00053 21.6943 6.92308 21.6867 6.85272 21.6575C6.78236 21.6284 6.72224 21.579 6.67999 21.5156C6.63773 21.4522 6.61525 21.3777 6.61538 21.3015Z" stroke={brandDetails.headerIconColor}/>
                          <path d="M6.61719 12.2306H17.3864M6.61719 7.61523H17.3864" stroke={brandDetails.headerIconColor} strokeLinecap="round"/>
                        </svg>
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="End Session" placement="top" arrow>
                      <IconButton onClick={() => setIsEndedSession(true)} className={`${isDisabledEndSessionBtn || sessionStorage.getItem("sessionEnded") === "true" ? 'opacity-50 cursor-not-allowed': ''}`} disabled={isDisabledEndSessionBtn || sessionStorage.getItem("sessionEnded") === "true"}>
                        <svg width={isMobile ? 24: 32} height={isMobile ? 24: 32} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" clipRule="evenodd" d="M18.8549 6.45104C18.9405 6.36563 19.0083 6.26421 19.0546 6.15258C19.101 6.04094 19.1248 5.92127 19.1249 5.80041C19.125 5.67954 19.1013 5.55985 19.0551 5.44815C19.0089 5.33646 18.9412 5.23496 18.8558 5.14944C18.7703 5.06393 18.6689 4.99607 18.5573 4.94975C18.4456 4.90343 18.326 4.87955 18.2051 4.87947C18.0843 4.8794 17.9646 4.90313 17.8529 4.94931C17.7412 4.9955 17.6397 5.06323 17.5541 5.14864L12.0037 10.699L6.45495 5.14864C6.28224 4.97593 6.048 4.87891 5.80375 4.87891C5.5595 4.87891 5.32526 4.97593 5.15255 5.14864C4.97984 5.32135 4.88281 5.55559 4.88281 5.79984C4.88281 6.04409 4.97984 6.27833 5.15255 6.45104L10.7029 11.9998L5.15255 17.5486C5.06703 17.6342 4.9992 17.7357 4.95291 17.8474C4.90663 17.9591 4.88281 18.0789 4.88281 18.1998C4.88281 18.3208 4.90663 18.4405 4.95291 18.5523C4.9992 18.664 5.06703 18.7655 5.15255 18.851C5.32526 19.0237 5.5595 19.1208 5.80375 19.1208C5.92469 19.1208 6.04444 19.097 6.15618 19.0507C6.26791 19.0044 6.36943 18.9366 6.45495 18.851L12.0037 13.3006L17.5541 18.851C17.7269 19.0235 17.961 19.1204 18.2051 19.1202C18.4492 19.1201 18.6833 19.0229 18.8558 18.8502C19.0282 18.6775 19.1251 18.4434 19.1249 18.1993C19.1248 17.9552 19.0277 17.7211 18.8549 17.5486L13.3045 11.9998L18.8549 6.45104Z" fill={brandDetails.headerIconColor} />
                        </svg>
                      </IconButton>
                    </Tooltip>
                  </div>
                </header>
                <main className={`${isMobile ? "h-[calc(100%-88px)]": "h-[calc(100%-176px)]"}`}>
                  <MainChatView sessionData={sessionData} brandDetails={brandDetails} isEndedSession={isEndedSession} onDisabledEndSessionBtn={handleDisabledEndSessionBtn} />
                </main>
              </> :
              <PageNotFound />
            }
          </div>
        </div>
        {OgMeta.title && OgMeta.description && (
          <HelmetMeta title={OgMeta.title} description={OgMeta.description} image={OgMeta.image} />
        )}
    </ThemeProvider>
  );
};

export default AgentChatWidget;
